// components/layout/Footer.js
import React, { useState } from 'react';
import Link from 'next/link'; // Keep Link for navigation if needed
import {
  PhoneIcon,
  EnvelopeIcon,
  HeartIcon // Keeping HeartIcon for 'made with love' sentiment
} from '@heroicons/react/24/outline'; // Named imports for icons
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';

const Footer = () => { // Define as a regular const
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated } = useAuth();
  const currentYear = new Date().getFullYear();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail.trim()) {
      toast.error('Veuillez entrer votre adresse email.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) {
      toast.error('Veuillez entrer une adresse email valide.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Store newsletter subscription locally for now
      const existingSubscriptions = JSON.parse(localStorage.getItem('newsletterSubscriptions') || '[]');
      
      // Check if email already exists
      if (existingSubscriptions.some(sub => sub.email === newsletterEmail)) {
        toast.error('Cette adresse email est déjà abonnée à notre newsletter.');
        setIsSubmitting(false);
        return;
      }

      const subscription = {
        email: newsletterEmail,
        subscribedAt: new Date().toISOString(),
        preferences: {
          newInventory: true,
          promotions: true,
          blogUpdates: true
        },
        status: 'active'
      };

      existingSubscriptions.push(subscription);
      localStorage.setItem('newsletterSubscriptions', JSON.stringify(existingSubscriptions));

      // Try to send to backend if available
      try {
        await fetch(`${process.env.NEXT_PUBLIC_API_URL || 'https://us-central1-fenparet-website.cloudfunctions.net/api'}/newsletter/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(subscription),
        });
      } catch (serverError) {
      }

      toast.success('Merci de vous être abonné! Vous recevrez des notifications pour les nouveaux arrivages.');
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-theme-secondary text-theme-primary pt-16 pb-8 border-t border-theme-border mt-auto theme-transition">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand & Mission */}
          <div className="flex flex-col space-y-6">
            <Link
              href="/"
              className="text-3xl font-bold tracking-tight text-accent font-serif"
            >
              Fenkparet
            </Link>
            <p className="text-theme-secondary leading-relaxed max-w-xs">
              Exprimer votre singularité à travers des produits artistiques et des créations uniques.
            </p>
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-theme-primary uppercase tracking-wider mb-4">
                Suivez-nous
              </h3>
              <div className="flex space-x-3 flex-wrap gap-y-2">
                <a href="https://www.facebook.com/share/1CMGWQkgvc/" target="_blank" rel="noopener noreferrer"
                  style={{ backgroundColor: '#1877F2' }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-85 transition-opacity text-white" title="Facebook" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/fenkparet?igsh=MWo0Mm1xZDgwNjlwaQ==" target="_blank" rel="noopener noreferrer"
                  style={{ background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)' }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-85 transition-opacity text-white" title="Instagram" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://x.com/FenkParet?t=ufb_v0B_D4Qj4AIc-G_8HQ&s=09" target="_blank" rel="noopener noreferrer"
                  style={{ backgroundColor: '#000000' }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-85 transition-opacity text-white" title="X (Twitter)" aria-label="X (Twitter)">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://youtube.com/@fenkparet2862?si=c46Fqko-Y0PIhQdS" target="_blank" rel="noopener noreferrer"
                  style={{ backgroundColor: '#FF0000' }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-85 transition-opacity text-white" title="YouTube" aria-label="YouTube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@fenkparet?_t=ZM-8xbHrr8LfYj&_r=1" target="_blank" rel="noopener noreferrer"
                  style={{ backgroundColor: '#010101' }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-85 transition-opacity text-white" title="TikTok" aria-label="TikTok">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
                <a href="https://wa.me/50947458821" target="_blank" rel="noopener noreferrer"
                  style={{ backgroundColor: '#25D366' }}
                  className="w-10 h-10 rounded-lg flex items-center justify-center hover:opacity-85 transition-opacity text-white" title="WhatsApp" aria-label="WhatsApp">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Boutique Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-theme-primary">Boutique</h4>
            <ul className="space-y-4">
              <li><Link href="/products" className="text-theme-secondary hover:text-accent transition-colors">Tous les produits</Link></li>
              <li><Link href="/nouveautes" className="text-theme-secondary hover:text-accent transition-colors">Nouveautés</Link></li>
            </ul>
          </div>

          {/* Aide & Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-theme-primary">Aide</h4>
            <ul className="space-y-4">
              <li><Link href="/contact" className="text-theme-secondary hover:text-accent transition-colors">Contact</Link></li>
              <li><Link href="/faq" className="text-theme-secondary hover:text-accent transition-colors">FAQ</Link></li>
              <li><Link href="/privacy" className="text-theme-secondary hover:text-accent transition-colors">Livraison & Retours</Link></li>
              <li>
                <div className="pt-2 flex items-center space-x-2 text-sm text-theme-tertiary">
                  <PhoneIcon className="w-4 h-4" width="16" height="16" />
                  <span>(+509) 4745-8821</span>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-1">
            <h4 className="font-bold text-lg mb-4 text-theme-primary">Rejoindre la Gazette</h4>
            <p className="text-theme-secondary mb-6 text-sm">
              Inscrivez-vous pour recevoir nos dernières collections et des offres exclusives.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <input
                  id="footer-newsletter-email"
                  name="email"
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="votre@email.com"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-theme-input border border-theme-border rounded-lg text-theme-primary placeholder-theme-tertiary transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-accent text-white rounded-lg font-bold hover:bg-accent-hover transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                <span>{isSubmitting ? 'En cours...' : "S'abonner"}</span>
                {!isSubmitting && <EnvelopeIcon className="w-4 h-4" width="16" height="16" />}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-theme-border flex flex-col md:flex-row justify-between items-center text-sm text-theme-tertiary gap-4">
          <p>&copy; {currentYear} Fenkparet. Tous droits réservés.</p>
          <div className="flex space-x-6">
            <Link href="/privacy" className="hover:text-accent transition-colors">Confidentialité</Link>
            <Link href="/terms" className="hover:text-accent transition-colors">Conditions</Link>
          </div>
          <p>
            Built by: <Link href="https://github.com/Roma10boss" className="font-medium text-accent hover:underline">Romario Gustave</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; // <-- Ensure this is the ONLY export for Footer
